import { Link } from 'react-router-dom';
import PublicHeader from '../PublicHeader';
import AppFooter from '../AppFooter';
import { useDocumentMeta } from '../../lib/useDocumentMeta';
import { getGuide, relatedGuides } from '../../lib/guides';

const formatUpdated = (isoDate) =>
  new Date(isoDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * Shared chrome for every /guides/:slug article: header, breadcrumb, a
 * green hero (pill tag, title, dek) matching ForOrganisations.jsx's hero
 * exactly, the article body (children — full-bleed GuideSections, not a
 * single narrow column), a related-guides rail, footer. Sets the page
 * title/meta description and an Article JSON-LD block from the guide's
 * registry entry (lib/guides.js).
 */
export const GuideLayout = ({ slug, children }) => {
  const guide = getGuide(slug);
  const path = `/guides/${slug}`;

  useDocumentMeta({
    title: guide ? `${guide.title} | Precious Data` : 'Guide | Precious Data',
    description: guide?.description,
    path,
  });

  const articleJsonLd = guide
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: guide.title,
        description: guide.description,
        dateModified: guide.updated,
        author: { '@type': 'Organization', name: 'Precious Data' },
        publisher: { '@type': 'Organization', name: 'Precious Data' },
        mainEntityOfPage: `https://preciousdata.org.uk${path}`,
      }
    : null;

  const related = relatedGuides(slug);

  return (
    <div className="min-h-screen bg-surface text-ink" data-testid="guide-page">
      {articleJsonLd && <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>}
      <PublicHeader />

      <nav aria-label="Breadcrumb" className="px-4 sm:px-7 lg:px-12 pt-2 pb-4 text-[14px] text-ink-55">
        <div className="max-w-3xl mx-auto">
          <Link to="/guides" className="hover:text-ink underline-offset-2 hover:underline" data-testid="guide-breadcrumb-link">
            Guides
          </Link>
          <span aria-hidden="true"> / </span>
          <span className="text-ink-70">{guide?.title || 'Guide'}</span>
        </div>
      </nav>

      {/* Hero — same shape as ForOrganisations.jsx: full-bleed rounded ground
          panel, brand-wash pill tag, big bold title, ink-70 dek. */}
      <section className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-11 lg:px-12">
        <div className="max-w-3xl mx-auto space-y-6">
          {guide?.category && (
            <span className="inline-block rounded-full bg-brand-wash text-brand-ink px-4 py-2 text-[15px] font-medium">
              {guide.category}
            </span>
          )}
          <h1 className="font-heading text-[40px] sm:text-[52px] font-bold tracking-[-0.04em] leading-[0.98]">
            {guide?.title}
          </h1>
          {guide?.dek && <p className="text-[19px] leading-[1.5] text-ink-70">{guide.dek}</p>}
          {guide?.updated && <p className="pd-mono text-ink-55">Last updated {formatUpdated(guide.updated)}</p>}
        </div>
      </section>

      <article>{children}</article>

      {related.length > 0 && (
        <section className="px-4 sm:px-7 lg:px-12 py-12">
          <div className="max-w-3xl mx-auto space-y-5">
            <p className="pd-eyebrow">Related guides</p>
            <ul className="space-y-3">
              {related.map((g) => (
                <li key={g.slug}>
                  <Link
                    to={`/guides/${g.slug}`}
                    className="block rounded-lg bg-ground px-5 py-4 hover:opacity-90 transition-opacity duration-state"
                    data-testid={`related-guide-${g.slug}`}
                  >
                    <p className="font-heading text-[17px] font-semibold tracking-[-0.02em] text-ink">{g.title}</p>
                    <p className="text-[14px] text-ink-55 mt-1">{g.dek}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <AppFooter />
    </div>
  );
};

export default GuideLayout;
